app.service("snomedConceptUtil", [
	"Base64", // Make sure you inject Base64 here!
	"$sanitize",
	function (Base64, $sanitize) {
		const LATERALITY_LABELS = {
			OD: 'Right eye',
			OS: 'Left eye',
			OU: 'Both eyes'
		};

		// Escape plain note text so it renders as text, never as markup.
		const escapeHtml = (str) => str
			.replace(/&/g, '&amp;')
			.replace(/</g, '&lt;')
			.replace(/>/g, '&gt;')
			.replace(/"/g, '&quot;')
			.replace(/'/g, '&#39;');

		// In sanitised HTML a match is only usable when it sits in text content - not inside a tag
		// or an entity - otherwise injecting a tooltip there would corrupt the markup.
		const isInHtmlText = (html, index, text) => {
			if (/[<>&]/.test(text)) return false;
			if (html.lastIndexOf('<', index) > html.lastIndexOf('>', index)) return false;
			const prev = html.charAt(index - 1);
			return prev !== '&' && prev !== '#';
		};

		var service = {

			processNotes: function (notes) {
				if (!notes || !Array.isArray(notes)) return [];

				notes.forEach(note => {
					if (note.progress_note_type === 'AI_CONVERSATION_NOTE' || note.progress_note_type === 'PROGRESS_NOTE') {

						let rawText = note.free_text || '';
						// Decoded progress notes are Quill HTML and get sanitised; everything else is plain
						// text and gets escaped, segment by segment, when the output is built below.
						let isHtml = false;

						// 1. DECODE Base64 and STRIP Quill JSON if it's a standard progress note
						if (note.progress_note_type === 'PROGRESS_NOTE' && rawText) {
							try {
								rawText = Base64.decode(rawText);

								// If it's a Quill Delta JSON, extract just the pure text
								if (rawText.trim().startsWith('{') && rawText.includes('"ops"')) {
									let delta = JSON.parse(rawText);
									let extractedText = '';
									delta.ops.forEach(op => {
										if (typeof op.insert === 'string') {
											extractedText += op.insert;
										}
									});
									rawText = extractedText;
								} else {
									// $sanitize re-encodes every non-ASCII character as a numeric entity, which would
									// stop a term with a macron or accent ("Māori", "Sjögren") from matching the text.
									// Those characters are put back - none of them can form markup, so the output
									// stays as safe as $sanitize left it. ASCII entities (quotes, & < >) are kept.
									rawText = $sanitize(rawText).replace(/&#(\d+);/g, function (entity, code) {
										const point = Number(code);
										return point > 126 ? String.fromCodePoint(point) : entity;
									});
									isHtml = true;
								}
							} catch (e) {
								console.warn("Failed to decode or parse progress note");
								isHtml = false;
							}
						}

						if (note.extracted_snomed_concepts && note.extracted_snomed_concepts.length > 0) {
							let matchMap = new Map();

							// PASS 1: Find and Group
							note.extracted_snomed_concepts.forEach(concept => {
								const term = (concept.text_excerpt || '').trim();
								const expectedStart = parseInt(concept.start_position, 10);

								if (!term || isNaN(expectedStart)) return;

								// Create a global, case-insensitive regex for the term
								const escapedTerm = term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
								const regex = new RegExp('\\b' + escapedTerm + '\\b', 'gi');

								let match;
								let bestMatch = null;
								let minDistance = Infinity;

								// Find ALL case-insensitive matches in the raw text
								while ((match = regex.exec(rawText)) !== null) {
									if (isHtml && !isInHtmlText(rawText, match.index, match[0])) {
										continue;
									}
									const distance = Math.abs(match.index - expectedStart);

									// Pick the match closest to the backend's expected start position
									if (distance < minDistance) {
										minDistance = distance;
										bestMatch = match;
									}
								}

								// If we found at least one match, register it
								if (bestMatch) {
									const actualStart = bestMatch.index;
									const actualEnd = actualStart + bestMatch[0].length;
									const targetText = bestMatch[0]; // Retains the exact casing from the raw text

									const key = `${actualStart}-${actualEnd}`;
									const conceptDesc = {
										code: concept.concept_id || '',
										term: concept.concept_term || '',
										laterality: LATERALITY_LABELS[concept.laterality] || concept.laterality || ''
									};

									if (matchMap.has(key)) {
										const existing = matchMap.get(key).concepts;
										const isDuplicate = existing.some(c => c.code === conceptDesc.code && c.laterality === conceptDesc.laterality);
										if (!isDuplicate) existing.push(conceptDesc);
									} else {
										matchMap.set(key, {
											start: actualStart,
											end: actualEnd,
											targetText: targetText,
											concepts: [conceptDesc]
										});
									}
								} else {
									console.warn("SNOMED Util: Term not found in text");
								}
							});

							// PASS 2: Pick non-overlapping matches (latest first, as before), then build the
							// output left to right, escaping the text between matches so the offsets found above
							// stay valid. Tooltip attributes are URI-encoded, so they carry no quotes, markup or
							// {{ }} for Angular to interpolate.
							let verifiedMatches = Array.from(matchMap.values()).sort((a, b) => b.start - a.start);
							let lastInjectedStart = rawText.length + 1;
							const accepted = [];

							verifiedMatches.forEach(m => {
								if (m.end <= lastInjectedStart) {
									accepted.unshift(m);
									lastInjectedStart = m.start;
								}
							});

							const escapeSegment = isHtml ? (str) => str : escapeHtml;
							let finalHtml = '';
							let position = 0;

							accepted.forEach(m => {
								const safeTarget = encodeURIComponent(m.targetText);
								const safeConcepts = encodeURIComponent(JSON.stringify(m.concepts));
								const tag = `<snomed-concept-tooltip target-text="${safeTarget}" concepts="${safeConcepts}"></snomed-concept-tooltip>`;

								finalHtml += escapeSegment(rawText.substring(position, m.start)) + tag;
								position = m.end;
							});
							finalHtml += escapeSegment(rawText.substring(position));

							// 3. Safe line break replacement AFTER HTML is injected
							note.highlighted_free_text = finalHtml.replace(/\n/g, '<br>');
						} else {
							note.highlighted_free_text = escapeSegmentFor(isHtml, rawText).replace(/\n/g, '<br>');
						}
					}
				});

				return notes;
			}
		};

		function escapeSegmentFor(isHtml, text) {
			return isHtml ? text : escapeHtml(text);
		}

		return service;
	},
]);
